import { HttpClientModule } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { ThemeModeService } from './_metronic/partials/layout/theme-mode-switcher/theme-mode.service';
import {
  LocalStorageService,
  PageVisitService,
  SharedService,
  UserService,
  VersionCheckService,
} from './services';
import { SignalRService } from './services/signalr.service';
import { MatDialog } from '@angular/material/dialog';
import { SettingService } from './services/setting.service';
import { TTKeyVaultSecrets } from './models/setting.model';
import { PageIntegrityCheckerService } from './core/services/page-integrity-checker.service';
import { ChatWidgetGateService } from '@core/services/chat-widget-gate.service';

@Component({
  selector: 'body[root]',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [RouterOutlet, HttpClientModule],
})
export class AppComponent implements OnInit, OnDestroy {
  private subscribed = new Subscription();
  previousUrl: string | null = null;
  currentUrl: string | null = null;

  constructor(
    private modeService: ThemeModeService,
    private signalRService: SignalRService,
    private _localStorageService: LocalStorageService,
    private _userService: UserService,
    private _settingService: SettingService,
    private _sharedService: SharedService,
    private versionCheckService: VersionCheckService,
    private router: Router,
    private dialog: MatDialog,
    private pageIntegrityCheckerService: PageIntegrityCheckerService,
    private pageVisitService: PageVisitService,
    private chatWidgetGateService: ChatWidgetGateService
  ) {
    const userToken = localStorage.getItem('authToken');
    const loggedInUser = this._localStorageService.getLoggedInUser();
    if (userToken && loggedInUser) {
      this.subscribed.add(
        this._settingService .getSecrets().subscribe((response) => {
          if (response.isSuccess) {
            this._sharedService.setSecretsValue(response.data || new TTKeyVaultSecrets());
          }

          // PCI DSS 11.6.1: one-time check of this page load's actual scripts and
          // security headers against the known-good baseline. See
          // PageIntegrityCheckerService for why this is gated to logged-in
          // sessions only. Defaults to enabled if the flag is missing (e.g. the
          // call above failed) - only an explicit false turns it off.
          if (response.data?.pageIntegrityCheckEnabled !== false) {
            this.pageIntegrityCheckerService.check();
          }
        })
      );

      this.subscribed.add(
        this._userService.getAccountDetails().subscribe((response) => {
          if (response.isSuccess) {
            let practiceId = response.data?.practiceInfo?.id || 0;
            if (practiceId) {
              this._sharedService.setIsAccountDetailsSaved(response?.data?.practiceInfo?.isAccountDetailsSaved ?? false)
              this._sharedService.setPracticeId(practiceId);
              this.signalRService.startConnection(
                userToken,
                Number(practiceId || 0)
              );
            }
          }
        })
      );
    }

      this.router.events.subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.dialog.closeAll();
        }
        // Task 3510: count the clicks on the Practice submenu items (logged-in users only).
        if (event instanceof NavigationEnd) {
          this.previousUrl = this.currentUrl;
          this.currentUrl = event.urlAfterRedirects;
          this._localStorageService.setData('previousUrl', this.previousUrl);
          if (localStorage.getItem('authToken'))
          {
            this.pageVisitService.onNavigated(event.urlAfterRedirects);
          }
        }
      });
  }

  ngOnInit() {
    this.modeService.init();
    this.versionCheckService.initVersionCheck('assets/version.json');
    this.chatWidgetGateService.init({
      preAuth: {
        src: 'https://api-folio.webstrail.com/folio.js',
        attributes: {
          'data-app-id': 'app_e3852d1cbba5456c',
          'data-bot-id': 'bot_cbebbf820a324cf9',
          'data-widget-key': 'wk_IDu8PdqJ52Id9DJWfbq2jzJ3-xdTEr9F',
          'data-api-base': 'https://api-folio.webstrail.com',
          'data-primary':'#02347B',
          'data-position':'bottom-left'
        },
      },
      postAuth: {
        src: 'https://api-folio.webstrail.com/folio.js',
        attributes: {
          'data-app-id': 'app_e3852d1cbba5456c',
          'data-bot-id': 'bot_828b6794320849df',
          'data-widget-key': 'wk_AapKjaPTTc27rElty7ktSXNQQ7PwPTpm',
          'data-api-base': 'https://api-folio.webstrail.com',
          'data-primary':'#0090DE',
          'data-position':'bottom-left'
        },
      },
    });
  }

  ngOnDestroy(): void {
    this.subscribed?.unsubscribe();
    this.signalRService?.stopConnection();
  }
}
